/**
 * /go-to-market-strategy-template — SEO landing page targeting the "go to
 * market strategy template" / "gtm template" / "go to market plan template"
 * cluster.
 *
 * Differentiation angle: a GTM strategy template in a slide deck or a Notion
 * doc is a fill-in-the-blanks artifact — and the run-up to launch is exactly
 * when its decisions move fastest (pricing flips, a channel gets cut, the
 * date slips). The static doc can't keep up, so positioning, pricing, and the
 * launch plan drift out of sync with each other before you ship. This page
 * hands the reader the actual template (the seven sections, spelled out) so a
 * searcher gets something usable, then shows that Draftlize turns each GTM
 * decision into a structured card where dependents auto-flag stale when an
 * upstream decision changes.
 *
 * English-only + inline (no i18n), mirroring DecisionLogTemplate.tsx and the
 * comparison pages. Chinese variant lives in GoToMarketStrategyTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function GoToMarketStrategyTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/go-to-market-strategy-template"
      documentTitle="Go-to-Market Strategy Template (That Doesn't Drift) · Draftlize"
      documentDescription="A go to market strategy template you can copy — Target market & ICP, Positioning & messaging, Pricing & packaging, Channels, Launch timeline, Success metrics, Key risks. The catch: a GTM template in a deck or a doc drifts the moment launch prep starts moving. In Draftlize every GTM decision is a structured card, and pricing, channels, and the timeline auto-flag stale when an upstream decision changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Go-to-Market</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A go-to-market strategy template<br/>
            <em>where the decisions don't drift before launch.</em>
          </h1>
          <p className="lede">
            You came for a <em>go to market strategy template</em> — the sections, the headings, something to drop into a deck or a doc before launch. It's below; take it. But know what you're getting: a GTM template is a fill-in-the-blanks artifact, and the run-up to launch is precisely when its decisions move fastest. Pricing flips, a channel gets cut, the date slips a week — and a slide or a Notion page has no idea that positioning, packaging, and the launch plan it recorded no longer line up. Draftlize gives you the same seven sections — and makes each decision a card an AI agent reads and writes, where everything downstream flags itself stale the moment one decision moves.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Seven sections. <em>Copy them anywhere.</em></h2>
          </div>
          <p>This is the whole go-to-market plan template. It works in a slide deck, a wiki, or a doc — it's the same shape most GTM plans converge on. Use it as-is. The next section is about what changes when each decision stops being a bullet and becomes a card.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Target market & ICP',
              hint: 'Who',
              body: 'The market you\'re entering and the ideal customer profile inside it — segment, company size, role, the specific pain you relieve. Everything else in the plan is downstream of this; get it wrong and the messaging, pricing, and channels all aim at the wrong person.',
            },
            {
              k: 'Positioning & messaging',
              hint: 'What you say',
              body: 'The one-line claim, the category you compete in, the alternatives you\'re displacing, and the proof. "For X who struggle with Y, we are the Z that does W." If the ICP shifts, this is the first thing that has to move with it.',
            },
            {
              k: 'Pricing & packaging',
              hint: 'How you charge',
              body: 'The model (per seat, per use, tiered, free trial), the tiers, and what sits in each. The single most volatile decision in launch prep — and the one the most other sections silently depend on, from messaging to the revenue model in your metrics.',
            },
            {
              k: 'Channels',
              hint: 'How they find you',
              body: 'The routes to the ICP — content and SEO, paid, outbound, partnerships, community, product-led. Which you lead with, which you test, which you ignore for now. Channels assume an ICP and a price point; change either and the channel mix is no longer the right one.',
            },
            {
              k: 'Launch timeline',
              hint: 'When',
              body: 'The sequence and dates — alpha, beta, GA, the launch moment itself, and the work that gates each. A timeline is a web of dependencies pretending to be a list; when one date slips, everything keyed to it should move, and on a slide nothing does.',
            },
            {
              k: 'Success metrics',
              hint: 'How you know',
              body: 'What "it worked" means in numbers — activation, conversion, CAC, payback, the north-star you\'re steering by. These are defined against your pricing and ICP, so a change upstream quietly invalidates the targets you set here.',
            },
            {
              k: 'Key risks',
              hint: 'What could break it',
              body: 'The assumptions that, if wrong, sink the plan — the channel that may not convert, the price the market may reject, the dependency that may slip. Naming them is cheap; the value is noticing the moment a decision elsewhere turns a risk into a fact.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the template. Below: why launch prep is the moment it starts to drift — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a GTM template <em>drifts.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The decisions move fastest right before launch</h3>
            <p>A GTM plan is at its most useful when it's stable — and launch prep is when it's least stable. Pricing flips twice, a channel gets cut after a bad test, the beta date slips. A slide deck captures one snapshot of a moving target, then quietly lies for the rest of the run-up.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The sections depend on each other, the doc doesn't know it</h3>
            <p>Change the pricing model and your messaging, your channel mix, and your success metrics are all suddenly out of date — because each was built on the old price. Nothing in a deck connects them, so they drift apart one edit at a time until the plan no longer agrees with itself.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Three docs, three versions of the truth</h3>
            <p>The pricing lives in a sheet, the positioning in a deck, the timeline in a project tool. By launch week each says something slightly different, and nobody can tell which one is current — so the team ships against a plan no single artifact actually states.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same template, <em>made live.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each section becomes a card, not a slide</h3>
            <p>In Draftlize the template isn't a blank deck — each GTM decision is a typed card: Target market & ICP, Positioning, Pricing & packaging, Channels, Launch timeline, Success metrics, Key risks. Structured, addressable by ID, and citable from a spec or a thread instead of buried in slide 14.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change pricing, the downstream flags stale</h3>
            <p>Because the links between decisions are real and not implied, changing the pricing decision turns the messaging, the channel mix, and the metrics that rested on it stale automatically — the way a build system invalidates everything downstream of a changed file. The plan can't drift out of agreement without telling you.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent reads and writes it</h3>
            <p>Claude Code or Cursor, over MCP, reads every relevant GTM decision before it drafts the launch brief, the pricing page, or the announcement — and writes new decisions back into the same shape. The plan stops being a deck nobody reopens and becomes context that gets read on every turn.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A go-to-market template tells you what to decide. It can't tell you when one of those decisions quietly broke the rest of the plan.
        </blockquote>
        <cite>Keep the seven sections. Let the substrate keep them in sync.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a go-to-market strategy?', a: 'A go-to-market strategy is the plan for how you will bring a product to market and reach customers: the target market and ICP, the positioning, the pricing and packaging, the channels, the launch timeline, the success metrics, and the key risks. It connects what you built to who will buy it and how they will hear about it.' },
            { q: 'What should a go-to-market strategy include?', a: 'The seven parts above: target market and ICP, positioning, pricing and packaging, channels, launch timeline, success metrics, and key risks. They interlock — pricing shapes channels, positioning shapes messaging — which is why a change to one quietly invalidates the others if nothing connects them.' },
            { q: 'What is the difference between a GTM strategy and a marketing plan?', a: 'A GTM strategy is the broader bet: who you serve, how you are positioned, how you price, and which channels reach them. A marketing plan is one execution layer under it — the campaigns and content that deliver the messaging. The GTM strategy decides the game; the marketing plan plays a part of it.' },
            { q: 'How does Draftlize keep a GTM plan in sync?', a: 'Each GTM decision is a card, and the links between them are real. Change the pricing decision and the messaging, channel mix, and metrics that rested on it flag themselves stale automatically — so the plan cannot drift out of agreement with itself without telling you.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start your GTM plan <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or spin up a project and let the agent fill it in from your existing launch docs — then keep every GTM decision, and everything that depends on it, in sync through launch and beyond.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
      <TemplateCta template="gtm" />
    </MarketingShell>
  );
}
